import { Row as RowSkeleton } from "@circle-react-uikit/SkeletonLoader/components/Row";
import { isPostDetailsPage } from "@circle-react/helpers/isPostDetailsPage";
import { toLocaleString } from "@circle-react/helpers/number";

import { t } from "@/i18n-js/instance";
import { useCurrentPostContext } from "@/react/contexts/currentPostContext";
import type { CSSAnchorAttributes } from "@/react/helpers/cssAnchorHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { useNavigateToPost } from "../PostModal/hooks/useNavigateToPost";
import { usePostNavigationContext } from "../PostViewList/PostNavigationContext";
import { useCurrentPostHelpers } from "../useCurrentPostHelpers";

interface CommentCountLinkProps {
  anchorProps?: CSSAnchorAttributes;
  count: number;
  hasPostDetailsLoaded: boolean;
  shouldForceSamePageInteractions?: boolean;
  toggleComment: () => void;
}

export const CommentCountLink = ({
  anchorProps,
  count,
  toggleComment,
  hasPostDetailsLoaded,
  shouldForceSamePageInteractions = false,
}: CommentCountLinkProps) => {
  const { post } = useCurrentPostHelpers();
  const { usedIn } = useCurrentPostContext();
  const { navigateToPost } = useNavigateToPost();
  const { params, hasNextPage } = usePostNavigationContext();

  /*
   * There's an edge case where the user has the post details page open and
   * they open the profile modal and navigate to the "Posts" tab. If they then
   * click the comment count link in one of the posts within the modal, we must
   * open a nested post modal to avoid issues with the infinite scroll. Otherwise,
   * loading comments for posts that are not at the bottom of the list won't work,
   * given the scrolling target is the whole modal and not the focused comments list.
   */
  const shouldShowPostModal =
    !isPostDetailsPage() || usedIn === "profile-modal";

  if (!hasPostDetailsLoaded) {
    return <RowSkeleton lg long />;
  }

  return (
    <button
      type="button"
      className={classNames(
        "p-0 text-sm font-medium text-default transition-colors duration-150 ease-in-out hover:!text-dark focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
      )}
      data-testid="post-comment-count"
      onClick={() => {
        if (shouldShowPostModal && !shouldForceSamePageInteractions) {
          navigateToPost({
            newPost: post,
            params,
            hasNextPage,
            shouldFocusOnCommentsSection: true,
          });
        } else {
          toggleComment();
        }
      }}
      {...anchorProps}
    >
      {t("post.comments", { count, formatted_count: toLocaleString(count) })}
    </button>
  );
};
